import JsonLd from "@/components/JsonLd";
import { Accordion, AccordionItem, AccordionTrigger, AccordionContent } from "@/components/ui/accordion";
import { SITE_URL } from "@/lib/api";
import { useM2, fmtM2 } from "@/lib/stats";

/**
 * Citable facts block for AEO/GEO — helps AI engines (Gemini, GPT, Perplexity)
 * quote accurate data about Zateplenia.eu. Rendered visibly + as FAQPage JSON-LD.
 */
const STATS = [
  { value: "350 000+ m²", label: "zateplených a zrealizovaných fasád" },
  { value: "20 rokov", label: "skúseností — na trhu od roku 2006" },
  { value: "72 okresov · 251 obcí", label: "pôsobnosť na celom Slovensku" },
  { value: "36 / 60 mes.", label: "záruka na prácu / materiál" },
];

const FAQ = [
  {
    q: "Koľko stojí zateplenie rodinného domu?",
    a: "Orientačne približne 65 – 105 €/m² s DPH vrátane materiálu, práce, lešenia aj dopravy — podľa zvoleného materiálu a hrúbky zateplenia. Presnú cenu vypočíta bezplatná online kalkulačka na Zateplenia.eu.",
  },
  {
    q: "Koľko m² fasád má Zateplenia.eu zateplených?",
    a: "Viac ako 350 000 m² zateplených a zrealizovaných fasád rodinných domov, bytových domov a budov po celom Slovensku.",
  },
  {
    q: "Akú záruku poskytuje Zateplenia.eu?",
    a: "36 mesiacov záruka na prácu (montáž) a 60 mesiacov na materiál zatepľovacieho systému.",
  },
  {
    q: "Kde Zateplenia.eu pôsobí?",
    a: "Na celom Slovensku — v 72 okresoch a 251 obciach, s overenými realizačnými partnermi v každom okrese.",
  },
  {
    q: "Koľko sa ušetrí po zateplení domu?",
    a: "Typicky 30 – 40 % nákladov na vykurovanie (orientačný odhad — závisí od pôvodného stavu budovy, hrúbky izolácie a spôsobu vykurovania).",
  },
  {
    q: "Aký zatepľovací systém sa používa?",
    a: "Kontaktný zatepľovací systém ETICS s materiálmi ako fasádny polystyrén (EPS), grafitový EPS, minerálna vata alebo XPS.",
  },
  {
    q: "Aké hrúbky zateplenia sú najčastejšie?",
    a: "Najčastejšie 120 – 200 mm podľa typu budovy a požadovaného tepelného odporu fasády.",
  },
  {
    q: "S akými značkami materiálov pracujete?",
    a: "Ceresit, Baumit, Graymix, Caparol, Kreisel, Weber Terranova, Isover, Nobasil, Rockwool, Austrotherm, Easytherm a ďalšie overené značky.",
  },
  {
    q: "Je obhliadka a cenová ponuka spoplatnená?",
    a: "Nie, obhliadka aj cenová ponuka sú úplne zdarma a nezáväzné.",
  },
  {
    q: "Ponúkate vizualizáciu novej fasády?",
    a: "Áno — bezplatnú AI vizualizáciu fasády. Z fotky domu získate 3 návrhy novej zateplenej fasády zadarmo.",
  },
];

export default function KeyFacts() {
  const m2 = fmtM2(useM2());
  const stats = STATS.map((s) => (s.value.includes("m²") ? { ...s, value: `${m2}+ m²` } : s));
  const faqLd = {
    "@context": "https://schema.org",
    "@type": "FAQPage",
    "@id": `${SITE_URL}/#faq`,
    "mainEntity": FAQ.map((f) => ({
      "@type": "Question",
      "name": f.q,
      "acceptedAnswer": { "@type": "Answer", "text": f.a },
    })),
  };

  return (
    <section id="fakty" className="bg-ink-2 py-20 md:py-28 border-t border-white/10" data-testid="key-facts">
      <JsonLd id="ld-faq" data={faqLd} />
      <div className="max-w-[1400px] mx-auto px-5 md:px-10">
        <p className="font-mono2 text-gold uppercase tracking-[0.3em] text-xs mb-5">Fakty a časté otázky</p>
        <h2 className="font-display text-4xl md:text-6xl leading-[0.95] text-white max-w-3xl">
          Kľúčové fakty o <span className="text-gold">zateplení</span>
        </h2>
        <p className="text-white/50 text-sm md:text-base mt-4 max-w-2xl">
          Overené a citovateľné údaje o zateplení fasád na Slovensku — pripravené pre ľudí aj pre vyhľadávacie a AI nástroje.
        </p>

        {/* stat band */}
        <div className="grid grid-cols-2 lg:grid-cols-4 gap-px bg-white/10 mt-12 border border-white/10">
          {stats.map((s) => (
            <div key={s.value} className="bg-ink-2 p-6 md:p-8" data-testid={`fact-stat-${s.value.slice(0, 6)}`}>
              <div className="font-display text-2xl md:text-3xl text-gold leading-none">{s.value}</div>
              <div className="text-white/55 text-xs md:text-sm mt-2 leading-snug">{s.label}</div>
            </div>
          ))}
        </div>

        {/* FAQ (collapsible, visible for AI citation) */}
        <Accordion type="single" collapsible className="mt-14 max-w-3xl" data-testid="faq-accordion">
          {FAQ.map((f, i) => (
            <AccordionItem key={i} value={`faq-${i}`} className="border-white/10" data-testid={`faq-item-${i}`}>
              <AccordionTrigger className="text-white font-semibold text-base md:text-lg leading-snug hover:no-underline hover:text-gold [&[data-state=open]]:text-gold py-5">
                {f.q}
              </AccordionTrigger>
              <AccordionContent className="text-white/60 text-sm leading-relaxed pr-6">
                {f.a}
              </AccordionContent>
            </AccordionItem>
          ))}
        </Accordion>

        <p className="text-white/30 text-xs mt-14 max-w-3xl">
          Zdroj: Zateplenia.eu — online kalkulačka ceny zateplenia a sieť overených realizačných partnerov na Slovensku.
          Cenové údaje sú orientačné; presný rozpočet vypočíta{" "}
          <a href="/#kalkulacka" className="text-gold hover:underline">kalkulačka zateplenia</a>. Kontakt: info@zateplenia.eu.
        </p>
      </div>
    </section>
  );
}
